import cp = require('child_process');
import * as lsp from 'vscode-languageserver';
import { TextDocument } from '@workbench-stack/language-core';
import { getBinPath, getFileArchive, getToolsEnvVars,
    SymbolInformation, Range } from './util';

// Keep in sync with https://github.com/ramya-rao-a/go-outline
export interface GoOutlineRange {
    start: number;
    end: number;
}

export interface GoOutlineDeclaration {
    label: string;
    type: string;
    receiverType?: string;
    icon?: string; // icon class or null to use the default images based on the type
    start: number;
    end: number;
    children?: GoOutlineDeclaration[];
    signature?: GoOutlineRange;
    comment?: GoOutlineRange;
}

export interface GoOutlineOptions {
    fileName: string;
    importsOnly?: boolean;
    document?: TextDocument;
}

export function documentSymbols(options: GoOutlineOptions): Promise<GoOutlineDeclaration[]> {
    return new Promise<GoOutlineDeclaration[]>((resolve, reject) => {
        let gooutline = getBinPath('go-outline');
        let gooutlineFlags = ['-f', options.fileName];
        if (options.importsOnly) {
            gooutlineFlags.push('-imports-only');
        }

        let p: cp.ChildProcess;

        // Spawn `go-outline` process
        p = cp.execFile(gooutline, gooutlineFlags, { env: getToolsEnvVars() }, (err, stdout, stderr) => {
            try {
                if (stderr && stderr.startsWith('flag provided but not defined: ')) {
                    console.log('go-outline', stderr);
                    if (stderr.startsWith('flag provided but not defined: -imports-only')) {
                        options.importsOnly = false;
                    }
                    if (stderr.startsWith('flag provided but not defined: -modified')) {
                        options.document = null;
                    }
                    p = null;
                    return documentSymbols(options).then(results => {
                        return resolve(results);
                    });
                }
                if (err) {return resolve(null); }
                let result = stdout.toString();
                let decls = <GoOutlineDeclaration[]>JSON.parse(result);
                return resolve(decls);
            } catch (e) {
                console.log('e', e);
                reject(e);
            }
        });
        if (options.document) {
            p.stdin.end(getFileArchive(options.document));
        }
    });
}

export class GoDocumentSymbolProvider {

    private goKindToCodeKind: { [key: string]: lsp.SymbolKind } = {
        'package': lsp.SymbolKind.Package,
        'import': lsp.SymbolKind.Namespace,
        'variable': lsp.SymbolKind.Variable,
        'type': lsp.SymbolKind.Interface,
        'function': lsp.SymbolKind.Function,
    };

    private convertToCodeSymbols(document, decls: GoOutlineDeclaration[], symbols: SymbolInformation[], containerName: string): void {
        let includeImports = false;

        (decls || []).forEach(decl => {
            if (!includeImports && decl.type === 'import') {return; }
            let label = decl.label;
            if (decl.receiverType) {
                label = '(' + decl.receiverType + ').' + label;
            }

            let codeBuf = new Buffer(document.getText());
            let start = codeBuf.slice(0, decl.start - 1).toString().length;
            let end = codeBuf.slice(0, decl.end - 1).toString().length;

            let symbolInfo = new SymbolInformation(
                label,
                this.goKindToCodeKind[decl.type],
                new Range(document.positionAt(start), document.positionAt(end)),
                document.filename,
                containerName);
            symbols.push(symbolInfo);
            if (decl.children) {
                this.convertToCodeSymbols(document, decl.children, symbols, decl.label);
            }
        });
    }

    public provideDocumentSymbols(document): Thenable<SymbolInformation[]> {
        let options = { fileName: document.fileName, document: document };
        return documentSymbols(options).then(decls => {
            let symbols: SymbolInformation[] = [];
            this.convertToCodeSymbols(document, decls, symbols, '');
            return symbols;
        });
    }
}
